/**
 * DataGridFieldAdapter - Adapts field components for MUI X Data Grid cell editing.
 *
 * This wrapper bridges the gap between standalone form field components and
 * the Data Grid's cell editing system by handling:
 * - Grid API integration (setEditCellValue, stopCellEditMode)
 * - Focus management when entering edit mode
 * - Keyboard handling (ESC to cancel, Enter to save)
 * - Prop transformation from GridRenderEditCellParams
 */

import React, { useCallback, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";

interface DataGridFieldAdapterProps extends GridRenderEditCellParams {
  /** The field component to render */
  children: (props: {
    value: unknown;
    onChange: (value: unknown) => void;
    onBlur: () => void;
    disabled: boolean;
    autoFocus: boolean;
    error?: string;
  }) => React.ReactNode;
  /** Whether this field is nullable */
  nullable?: boolean;
  /** Callback to transform value before saving */
  transformValue?: (value: unknown) => unknown;
}

/**
 * Adapter component that wraps field components for Data Grid editing
 */
export function DataGridFieldAdapter({
  id,
  field,
  value: initialValue,
  children,
  transformValue,
}: DataGridFieldAdapterProps) {
  const apiRef = useGridApiContext();
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the first input when entering edit mode
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const input = containerRef.current.querySelector(
          "input, textarea, [tabindex]",
        );
        if (input instanceof HTMLElement) {
          input.focus();
        }
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = useCallback(
    (newValue: unknown) => {
      const finalValue = transformValue ? transformValue(newValue) : newValue;
      apiRef.current.setEditCellValue({ id, field, value: finalValue });
    },
    [apiRef, id, field, transformValue],
  );

  const handleBlur = useCallback(() => {
    // Stop editing when the field loses focus
    // Small delay to allow click events on other elements to process first
    setTimeout(() => {
      // Check if cell is still in edit mode before stopping
      // This prevents errors when the cell was already stopped by another action
      try {
        const cellMode = apiRef.current.getCellMode(id, field);
        if (cellMode === "edit") {
          apiRef.current.stopCellEditMode({ id, field });
        }
      } catch {
        // Cell might not exist anymore (e.g., row was deleted/saved)
      }
    }, 100);
  }, [apiRef, id, field]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        apiRef.current.stopCellEditMode({
          id,
          field,
          ignoreModifications: true,
        });
      } else if (event.key === "Enter" && !event.shiftKey) {
        event.stopPropagation();
        apiRef.current.stopCellEditMode({ id, field });
      }
    },
    [apiRef, id, field],
  );

  return (
    <Box
      ref={containerRef}
      onKeyDown={handleKeyDown}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      {children({
        value: initialValue,
        onChange: handleChange,
        onBlur: handleBlur,
        disabled: false,
        autoFocus: true,
      })}
    </Box>
  );
}

export default DataGridFieldAdapter;
